import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExpandableText from 'components/common/ExpandableText';
import TableRowAction from 'components/common/TableRowAction';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import React, { useState, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import BeneficiaryFeedbackFormModal from './BeneficiaryFeedbackFormModal';
import { format } from 'date-fns';
import { useAuthContext } from '../../../context/authContext';
import { useBeneficiaryContext } from 'context/beneficiaryContext';
import LinkedReminders from 'components/reminders/LinkedReminders';
import {
  useBeneficiaryFeedback,
  useCreateBeneficiaryFeedback,
  useUpdateBeneficiaryFeedback,
  useDeleteBeneficiaryFeedback
} from 'hooks/useBeneficiaries';

const BeneficiaryFeedback = () => {
  const {
    user: { id: userId }
  } = useAuthContext();
  const { id } = useParams();
  const { beneficiary } = useBeneficiaryContext();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deleteId, setDeleteId] = useState();
  const [showAddFeedbackModel, setShowFeedbackModel] = useState(false);
  const [feedback, setFeedback] = useState({});

  const columns = useMemo(
    () => [
      {
        accessor: 'staff_name',
        Header: 'Staff Name',
        cellProps: { className: 'py-2' },
        Cell: rowData =>
          rowData.row.original.user
            ? `${rowData.row.original.user.first_name} ${rowData.row.original.user.last_name}`
            : '---'
      },
      {
        accessor: 'created_at',
        Header: 'Date',
        cellProps: { className: 'py-2' },
        Cell: rowData => (
          <p
            className="text-truncate mb-0"
            style={{ maxWidth: '400px', fontSize: '1rem' }}
          >
            {format(
              new Date(rowData.row.original.created_at),
              'dd-MM-yyyy, hh:mm a'
            )}
          </p>
        )
      },
      {
        accessor: 'description',
        Header: 'Note',
        cellProps: { className: 'py-2' },
        Cell: rowData => (
          <div className="d-flex align-items-start gap-2">
            {rowData.row.original.is_pinned && (
              <FontAwesomeIcon
                icon="thumbtack"
                className="text-warning mt-1"
                title="Pinned"
              />
            )}
            <ExpandableText text={rowData.row.original.description} />
          </div>
        )
      },
      {
        accessor: 'note_type',
        Header: 'Type',
        cellProps: { className: 'py-2' },
        Cell: rowData => rowData.row.original.note_type || '—'
      },
      {
        accessor: 'priority',
        Header: 'Priority',
        cellProps: { className: 'py-2' },
        Cell: rowData => (
          <p
            className={classNames('mb-0', {
              'text-danger': rowData.row.original.priority === 'Important'
            })}
            style={{ fontSize: '1rem' }}
          >
            {rowData.row.original.priority}
          </p>
        )
      },
      {
        accessor: 'id',
        Header: '',
        cellProps: { className: 'py-2 text-end' },
        disableSortBy: true,
        Cell: rowData => {
          const { id: rowId } = rowData.row;
          return (
            <TableRowAction
              onCLickEdit={() => {
                setShowFeedbackModel(true);
                setFeedback(rowData.row.original);
              }}
              onCLickDelete={() => {
                setDeleteId(rowId);
                setShowConfirmation(true);
              }}
              subject={permissionsKeyMap.sponsorship.children.beneficiaries}
            />
          );
        }
      }
    ],
    []
  );

  const { feedback: beneficiaryFeedbackData, isLoading } =
    useBeneficiaryFeedback(id);

  const { createFeedbackAsync, isCreating: addFeedbackModel } =
    useCreateBeneficiaryFeedback();
  const { updateFeedbackAsync } = useUpdateBeneficiaryFeedback();
  const { deleteFeedbackAsync } = useDeleteBeneficiaryFeedback();

  const handleAddNew = () => {
    setFeedback({});
    setShowFeedbackModel(true);
  };

  const handleSaveNote = async values => {
    try {
      if (Object.keys(feedback).length !== 0) {
        await updateFeedbackAsync({
          id: values.id,
          data: {
            user_id: userId,
            description: values.description,
            priority: values.priority,
            note_type: values.note_type || null,
            is_pinned: Boolean(values.is_pinned)
          }
        });
      } else {
        await createFeedbackAsync({
          orphan_id: id,
          user_id: userId,
          description: values.description,
          priority: values.priority,
          note_type: values.note_type || null,
          is_pinned: Boolean(values.is_pinned)
        });
      }
      setFeedback({});
      setShowFeedbackModel(false);
    } catch (error) {
      console.error('Error saving note:', error);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteFeedbackAsync(deleteId);
      setShowConfirmation(false);
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  return (
    <>
      {showAddFeedbackModel && (
        <BeneficiaryFeedbackFormModal
          show={showAddFeedbackModel}
          onHide={() => setShowFeedbackModel(false)}
          onSubmit={handleSaveNote}
          loading={addFeedbackModel}
          isEdit={Object.keys(feedback).length !== 0}
          initialValues={feedback}
        />
      )}
      {showConfirmation && (
        <ConfirmationModal
          show={showConfirmation}
          handleConfirm={handleConfirmDelete}
          onHide={() => setShowConfirmation(false)}
          title={'Confirmation'}
          body="You sure to delete this note?"
        />
      )}

      <LinkedReminders
        entityType="beneficiary"
        entityId={id}
        entityLabel={
          beneficiary
            ? `${beneficiary.first_name} ${beneficiary.last_name}`
            : ''
        }
      />

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={[...(beneficiaryFeedbackData || [])].sort(
              (a, b) =>
                Number(Boolean(b.is_pinned)) - Number(Boolean(a.is_pinned)) ||
                new Date(b.created_at) - new Date(a.created_at)
            )}
            columns={columns}
            title="Beneficiary Notes"
            showSearchInput
            searchInputPlaceHolder="Search"
            addNew
            onClickAddNew={handleAddNew}
            perPage={50}
            loading={isLoading}
            subject={permissionsKeyMap.sponsorship.children.beneficiaries}
          />
        </Col>
      </Row>
    </>
  );
};

export default BeneficiaryFeedback;
